import { useMutation, useQueryClient } from '@tanstack/react-query';
import { branchOfficeService } from '../services/branchOfficeService';
import { useReportError, extractErrorInfo } from '@features/support';
import type {
  CreateAddressPayload,
  CreateBranchOfficePayload,
  GeolocationData,
  AddressResponse,
  BranchOffice,
} from '../types/branchOffice.types';

/**
 * Hook to fetch geolocation data by coordinates
 */
/**
 * Traduce a lenguaje de usuario los errores del alta/edición de sucursal.
 *
 * Vivía dentro de `BranchOfficeFormModal`, donde alimentaba un `Swal` propio.
 * Se movió acá para que el `onError` de la mutación —que es quien abre el modal
 * de error del portal— pueda usar el mismo texto, y así el usuario vea UN solo
 * diálogo con el mensaje bueno en vez de dos: el crudo del modal del asistente
 * y el traducido del Swal. El modal sigue usándolo para el camino de EDICIÓN,
 * que no pasa por `reportError` y por lo tanto no duplica nada.
 */
export function getFriendlyErrorMessage(error: any, fallback = 'Error desconocido'): string {
  const status = error?.response?.status;
  let message =
    error?.response?.data?.message ||
    error?.response?.data?.details ||
    error?.message ||
    fallback;
  const lower = (message || '').toLowerCase();
  if (
    status === 409 ||
    lower.includes('duplicate') ||
    lower.includes('already exists') ||
    lower.includes('ya existe') ||
    lower.includes('código')
  ) {
    message = 'Ya existe una sucursal con este código. Por favor, utiliza un código diferente.';
  } else if (lower.includes('request failed with status code')) {
    // No mostrar el mensaje crudo de axios al usuario
    message = fallback;
  }
  return message;
}

export function useGeolocationByCoordinates() {
  return useMutation<GeolocationData, Error, { latitude: string; longitude: string }>({
    mutationFn: ({ latitude, longitude }) =>
      branchOfficeService.getGeolocationByCoordinates(latitude, longitude),
  });
}

/**
 * Hook to create an address
 */
export function useCreateAddress() {
  return useMutation<AddressResponse, Error, CreateAddressPayload>({
    mutationFn: (payload) => branchOfficeService.createAddress(payload),
  });
}

/**
 * Hook to create a branch office
 */
export function useCreateBranchOffice() {
  const queryClient = useQueryClient();
  // Asistente de soporte: explica el fallo del alta en vez del error crudo.
  const reportError = useReportError();

  return useMutation<BranchOffice, Error, CreateBranchOfficePayload>({
    mutationFn: (payload) => branchOfficeService.createBranchOffice(payload),
    onSuccess: () => {
      // Invalidate branch offices query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['branchOffices'] });
    },
    onError: (error: any) => {
      const info = extractErrorInfo(error);
      reportError({
        title: 'Error al crear sucursal',
        screen: 'crear sucursal',
        ...info,
        // Después del spread para pisar el texto crudo de `info`.
        message: getFriendlyErrorMessage(error, info.message),
      });
    },
  });
}
